import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type BankAccountResponse,
  type BankMovementResponse,
  type BankMovementsResponse,
  constantDifference,
  formatIban,
  formatMoney,
  reconciliationCheck,
  transferPending,
  reconciliationWhat,
  fromCents,
  MOVEMENT_TYPE_LABELS,
  parseDecimal,
  MOVEMENT_TYPES,
  type MovementType,
  MANUAL_LOAD_NAME,
  type ManualMovementRequest,
  type StatementLoadResult,
  toCents,
} from '@k-management/shared';
import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useCan } from '../../auth/AuthProvider.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { DataGrid, type GridColumn } from '../../components/DataGrid.js';
import { type Command, useCommands, usePageTitle } from '../../components/layout/page-chrome.js';
import { DecimalInput } from '../../components/DecimalInput.js';
import { Field } from '../../components/Field.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate, formatTimestamp } from '../../lib/format.js';
import { CsvExportDialog } from './CsvExportDialog.js';
import { ReconcilePanel, reconcileWindowPath } from './ReconcilePanel.js';
import { StartWorkDialog } from './StartWorkDialog.js';
import { postTreasuryMessage, useTreasuryChannel, worklistKeys } from './treasury-ui.js';
import { useListFilters } from '../../lib/list-filters.js';

type StatusFilter =
  | 'all'
  | 'da_riconciliare'
  | 'fatture'
  | 'stipendi'
  | 'tfr'
  | 'controllare'
  | 'mutuo'
  | 'anticipo'
  | 'carte'
  | 'spese'
  | 'rateazione'
  | 'motivo';

const STATUS_LABELS: Record<Exclude<StatusFilter, 'all'>, string> = {
  da_riconciliare: 'Da riconciliare',
  fatture: 'Con fatture',
  stipendi: 'Stipendi',
  tfr: 'TFR ai fondi',
  controllare: 'Da completare o controllare',
  mutuo: 'Mutui e finanziamenti',
  anticipo: 'Anticipi fatture',
  carte: 'Carte di credito',
  spese: 'Spese bancarie',
  rateazione: 'Rateazioni',
  motivo: 'Altro, con il motivo',
};

/** Whether a movement is reconciled, and with what. */
function ReconciliationCell({ movement }: { movement: BankMovementResponse }) {
  const done = movement.reconciliation;
  if (!done) {
    return (
      <>
        <span className="pill warn">da riconciliare</span>
        {movement.proposal && (
          <small className="muted block">proposta: {movement.proposal.number}</small>
        )}
      </>
    );
  }
  const what = reconciliationWhat(done);
  // Invoices, salaries or TFR that do not add up to the movement (1.69, invoices 1.76).
  const check = reconciliationCheck(movement.amount, done);
  // A transfer without the movement of its other account (3.13).
  const pending = transferPending(done);
  const short = (check !== null && toCents(check.unassigned) !== 0) || pending;
  return (
    <>
      <span className={short ? 'reconciled partial' : 'reconciled'}>
        <span aria-hidden="true">{short ? '◐' : '✓'}</span> {what}
      </span>
      {pending && <small className="warn block">da abbinare all’altro conto</small>}
      {check && short && (
        <small className="warn block">
          assegnati {formatMoney(check.assigned)} su{' '}
          {formatMoney(fromCents(Math.abs(toCents(movement.amount))))}
          {toCents(check.unassigned) > 0
            ? ` · mancano ${formatMoney(check.unassigned)}`
            : ` · superano di ${formatMoney(fromCents(-toCents(check.unassigned)))}`}
        </small>
      )}
      {check?.overpaid.map((label) => (
        <small key={label} className="warn block">
          {label}: pagato più del netto
        </small>
      ))}
    </>
  );
}

const TYPE_PILLS: Record<MovementType, string> = {
  entrata: 'pill ok',
  uscita: 'pill info',
  addebito: 'pill info',
  finanziamento: 'pill warn',
  spese: 'pill muted',
  altro: 'pill muted',
};

/**
 * The movements of a bank account (07/10/2026): loaded from the XML of the
 * statements, with the balance computed by K-Management and checked against
 * the bank day by day. The matching to the invoices comes next.
 */
export function BankMovementsPage() {
  const { code = '' } = useParams();
  const canWrite = useCan('tesoreria.write');
  const queryClient = useQueryClient();
  const banks = useQuery({
    queryKey: ['treasury', 'banks'],
    queryFn: () => apiRequest<BankAccountResponse[]>('/treasury/banks'),
  });
  const account = banks.data?.find((item) => item.code === code);
  usePageTitle(account ? `${account.alias} · movimenti` : 'Movimenti');
  const key = ['treasury', 'banks', code, 'movements'];
  const data = useQuery({
    queryKey: key,
    queryFn: () => apiRequest<BankMovementsResponse>(`/treasury/banks/${code}/movements`),
  });
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  // The filters stay while the browser is open: found again coming back to the list (1.61).
  const { filters, setFilter, reset, changed, changedClass } = useListFilters(`movements-${code}`, {
    type: 'all' as 'all' | MovementType,
    status: 'all' as StatusFilter,
    year: 'all' as string,
    month: 'all' as string,
  });
  const { type, status, year, month } = filters;
  const setType = (value: 'all' | MovementType) => setFilter('type', value);
  const setStatus = (value: StatusFilter) => setFilter('status', value);
  const setYear = (value: string) => setFilter('year', value);
  const setMonth = (value: string) => setFilter('month', value);
  const [openId, setOpenId] = useState<string | null>(null);
  // The separate window of the reconciliation, once the panel is detached.
  const detached = useRef<Window | null>(null);
  useTreasuryChannel();
  // Year, then month (1.51): the list of every month got too long.
  const [adding, setAdding] = useState(false);
  const [exporting, setExporting] = useState(false);
  useCommands([
    ...(canWrite
      ? [
          {
            key: 'load',
            label: 'Carica estratto conto',
            icon: 'upload',
            primary: true,
            onClick: () => setLoading(true),
          } satisfies Command,
          {
            key: 'manual',
            label: 'Movimento a mano',
            icon: 'plus',
            onClick: () => setAdding(true),
          } satisfies Command,
        ]
      : []),
    {
      key: 'csv',
      label: 'Esporta CSV',
      icon: 'down',
      onClick: () => setExporting(true),
    } satisfies Command,
  ]);
  const remove = useMutation({
    mutationFn: (loadId: string) =>
      apiRequest(`/treasury/banks/${code}/statements/${loadId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      await queryClient.invalidateQueries({ queryKey: ['treasury', 'banks'] });
    },
  });

  const movements = data.data?.movements ?? [];
  // A movement opened from an invoice, a card or a loan (2.07): only it is shown,
  // the filters and the search remembered for the list stay for «Mostra tutti».
  const [params, setParams] = useSearchParams();
  const focusId = params.get('movimento');
  const focused = focusId ? (movements.find((item) => item.id === focusId) ?? null) : null;
  const showAll = () => setParams({});
  const open = movements.find((item) => item.id === openId) ?? null;
  const openMovement = (item: BankMovementResponse) => {
    const target = detached.current;
    if (target && !target.closed) {
      postTreasuryMessage({ type: 'open', code, movementId: item.id });
      target.focus();
    } else setOpenId(item.id);
  };
  const detach = (item: BankMovementResponse) => {
    const target = window.open(
      reconcileWindowPath(code, item.id),
      'k-management-riconciliazione',
      'popup,width=640,height=860',
    );
    // A blocked window leaves the panel where it is.
    if (!target) return;
    detached.current = target;
    setOpenId(null);
  };
  const years = [...new Set(movements.map((item) => item.bookingDate.slice(0, 4)))]
    .sort()
    .reverse();
  const months = [
    ...new Set(
      movements
        .filter((item) => item.bookingDate.startsWith(year))
        .map((item) => item.bookingDate.slice(5, 7)),
    ),
  ].sort();
  const inMonth = movements.filter(
    (item) =>
      (year === 'all' || item.bookingDate.startsWith(year)) &&
      (month === 'all' || item.bookingDate.slice(5, 7) === month),
  );
  const period = year === 'all' ? 'caricate' : month === 'all' ? `del ${year}` : 'del mese';
  const statusOf = (item: BankMovementResponse): Exclude<StatusFilter, 'all'> =>
    !item.reconciliation
      ? 'da_riconciliare'
      : reconciliationCheck(item.amount, item.reconciliation) ||
          transferPending(item.reconciliation)
        ? 'controllare'
        : item.reconciliation.kind === 'motivo' && item.reconciliation.reason === 'tfr'
          ? 'tfr'
          : item.reconciliation.kind;
  const counts = inMonth.reduce(
    (total, item) => ({ ...total, [statusOf(item)]: (total[statusOf(item)] ?? 0) + 1 }),
    {} as Partial<Record<StatusFilter, number>>,
  );
  const rows = inMonth
    .filter(
      (item) =>
        (type === 'all' || item.type === type) && (status === 'all' || statusOf(item) === status),
    )
    .reverse();
  const sum = (items: BankMovementResponse[]) =>
    fromCents(items.reduce((total, item) => total + toCents(item.amount), 0));
  const check = data.data?.check;

  const columns: GridColumn<BankMovementResponse>[] = [
    {
      key: 'date',
      label: 'Data',
      value: (item) => item.bookingDate,
      render: (item) => formatDate(item.bookingDate),
    },
    {
      key: 'valueDate',
      label: 'Valuta',
      hiddenByDefault: true,
      value: (item) => item.valueDate ?? '',
      render: (item) => (item.valueDate ? formatDate(item.valueDate) : ''),
    },
    {
      key: 'type',
      label: 'Tipo',
      value: (item) => MOVEMENT_TYPE_LABELS[item.type],
      render: (item) => (
        <>
          <span className={TYPE_PILLS[item.type]}>{MOVEMENT_TYPE_LABELS[item.type]}</span>
          {item.abiCode && <small className="muted block">causale {item.abiCode}</small>}
        </>
      ),
    },
    {
      key: 'description',
      label: 'Descrizione della banca',
      required: true,
      value: (item) => `${item.description} ${item.counterparty ?? ''}`,
      render: (item) => (
        // The bank writes a lot: three lines here, the whole text on hover.
        <span className="movement-text" title={item.description}>
          {item.manual && <span className="pill muted">a mano</span>}{' '}
          {item.counterparty && !item.description.includes(item.counterparty) && (
            <strong className="block">{item.counterparty}</strong>
          )}
          {item.description}
        </span>
      ),
    },
    {
      key: 'amount',
      label: 'Importo',
      align: 'right',
      value: (item) => Number(item.amount),
      render: (item) => (
        <span className={Number(item.amount) < 0 ? 'negative' : undefined}>
          {formatMoney(item.amount)}
        </span>
      ),
    },
    {
      key: 'balance',
      label: 'Saldo',
      align: 'right',
      value: (item) => Number(item.balance),
      render: (item) => formatMoney(item.balance),
    },
    {
      key: 'reconciliation',
      label: 'Riconciliazione',
      value: (item) => STATUS_LABELS[statusOf(item)],
      render: (item) => <ReconciliationCell movement={item} />,
    },
  ];

  return (
    <section className="stack">
      <div className="page-header">
        <div>
          <p className="muted small">
            <Link to="/tesoreria/banche">Banche</Link> › {account?.alias ?? code}
          </p>
          <h1>{account ? `${account.alias} · movimenti` : 'Movimenti'}</h1>
          {account?.iban && <p className="muted">{formatIban(account.iban)}</p>}
        </div>
      </div>
      {data.isError && <p className="error">{data.error.message}</p>}
      {check && (
        <>
          <div className="treasury-tiles">
            <div className="treasury-tile">
              <span>Saldo{check.balanceDate ? ` al ${formatDate(check.balanceDate)}` : ''}</span>
              <strong>{check.balance !== null ? formatMoney(check.balance) : '—'}</strong>
            </div>
            <div className="treasury-tile">
              <span>Entrate {period}</span>
              <strong>{formatMoney(sum(inMonth.filter((item) => Number(item.amount) > 0)))}</strong>
            </div>
            <div className="treasury-tile">
              <span>Uscite {period}</span>
              <strong>{formatMoney(sum(inMonth.filter((item) => Number(item.amount) < 0)))}</strong>
            </div>
            <div className="treasury-tile">
              <span>Spese bancarie {period}</span>
              <strong>{formatMoney(sum(inMonth.filter((item) => item.type === 'spese')))}</strong>
            </div>
          </div>
          {check.movements > 0 &&
            (check.mismatches.length === 0 ? (
              <p className="notice info small">
                Il saldo torna con quello della banca in tutti i {check.checkedDays} giorni
                dell’estratto.
                {check.start?.source === 'banca' &&
                  ` I movimenti partono dal ${formatDate(check.start.date)}: il saldo di partenza è quello della banca a quella data (mancano i movimenti precedenti).`}
              </p>
            ) : (
              <MismatchNotice check={check} />
            ))}
        </>
      )}
      {focused && (
        <div className="notice info row between">
          <span>
            Stai guardando{' '}
            <strong>
              il movimento del {formatDate(focused.bookingDate)} · {formatMoney(focused.amount)}
            </strong>
            , arrivato da un collegamento.
          </span>
          <button type="button" className="small" onClick={showAll}>
            Mostra tutti i movimenti
          </button>
        </div>
      )}
      {focusId && data.data && !focused && (
        <div className="notice row between">
          <span>Il movimento del collegamento non c’è più: sotto ci sono tutti i movimenti.</span>
          <button type="button" className="small" onClick={showAll}>
            Chiudi
          </button>
        </div>
      )}
      {data.data && (
        <DataGrid
          id={`movements-${code}`}
          filtersChanged={focused ? false : changed}
          onResetFilters={reset}
          columns={columns}
          rows={focused ? [focused] : rows}
          searchable={!focused}
          rowClassName={(item) => (item.id === focusId ? 'best' : undefined)}
          rowKey={(item) => item.id}
          onRowOpen={openMovement}
          openOnClick
          searchPlaceholder="Descrizione, controparte o importo"
          empty={
            movements.length === 0
              ? canWrite
                ? 'Nessun movimento: caricalo con «Carica estratto conto».'
                : 'Nessun movimento caricato.'
              : 'Nessun movimento con questi filtri.'
          }
          toolbar={
            focused ? undefined : (
              <>
                <select
                  className={changedClass('year')}
                  aria-label="Anno"
                  value={year}
                  onChange={(event) => {
                    setYear(event.target.value);
                    setMonth('all');
                  }}
                >
                  <option value="all">Tutti gli anni</option>
                  {years.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
                <select
                  className={changedClass('month')}
                  aria-label="Mese"
                  value={month}
                  disabled={year === 'all'}
                  title={year === 'all' ? 'Scegli prima l’anno' : undefined}
                  onChange={(event) => setMonth(event.target.value)}
                >
                  <option value="all">Tutti i mesi</option>
                  {months.map((item) => (
                    <option key={item} value={item}>
                      {new Date(`2000-${item}-01T12:00:00`).toLocaleDateString('it-IT', {
                        month: 'long',
                      })}
                    </option>
                  ))}
                </select>
                <select
                  className={changedClass('status')}
                  aria-label="Riconciliazione"
                  value={status}
                  onChange={(event) => setStatus(event.target.value as StatusFilter)}
                >
                  <option value="all">Riconciliazione: tutti</option>
                  {(Object.keys(STATUS_LABELS) as Exclude<StatusFilter, 'all'>[]).map((item) => (
                    <option key={item} value={item}>
                      {STATUS_LABELS[item]} ({counts[item] ?? 0})
                    </option>
                  ))}
                </select>
                <select
                  className={changedClass('type')}
                  aria-label="Tipo"
                  value={type}
                  onChange={(event) => setType(event.target.value as 'all' | MovementType)}
                >
                  <option value="all">Tutti i tipi</option>
                  {MOVEMENT_TYPES.map((item) => (
                    <option key={item} value={item}>
                      {MOVEMENT_TYPE_LABELS[item]}
                    </option>
                  ))}
                </select>
              </>
            )
          }
        />
      )}
      {data.data && data.data.loads.length > 0 && (
        <details className="card loads-card">
          <summary>
            <strong>Estratti caricati</strong>{' '}
            <span className="muted">({data.data.loads.length})</span>
          </summary>
          <ul className="plain-list">
            {data.data.loads.map((load) => (
              <li key={load.id} className="row between">
                <span>
                  {load.fileName}
                  <small className="muted block">
                    {load.from && load.to
                      ? `${formatDate(load.from)} – ${formatDate(load.to)} · `
                      : ''}
                    {load.created} movimenti nuovi · {formatTimestamp(load.loadedAt)}
                    {load.loadedBy ? ` · ${load.loadedBy}` : ''}
                  </small>
                </span>
                {canWrite && load.created > 0 && (
                  <button type="button" className="link" onClick={() => setRemoving(load.id)}>
                    Togli
                  </button>
                )}
              </li>
            ))}
          </ul>
        </details>
      )}
      {loading && <LoadDialog code={code} onClose={() => setLoading(false)} />}
      {open && (
        <ReconcilePanel
          code={code}
          movement={open}
          onClose={() => setOpenId(null)}
          onDetach={() => detach(open)}
        />
      )}
      {adding && <ManualMovementDialog code={code} onClose={() => setAdding(false)} />}
      {exporting && (
        <CsvExportDialog
          title="Esporta i movimenti in CSV"
          subtitle={`${account?.alias ?? code} · per il commercialista`}
          path={`/treasury/banks/${encodeURIComponent(code)}/movements/csv`}
          note="Tutti i movimenti del periodo, senza i filtri della pagina."
          fallbackName="movimenti.csv"
          onClose={() => setExporting(false)}
        />
      )}
      {removing && (
        <ConfirmDialog
          title={
            data.data?.loads.find((item) => item.id === removing)?.fileName === MANUAL_LOAD_NAME
              ? 'Togliere il movimento a mano?'
              : 'Togliere questo estratto?'
          }
          confirmLabel="Togli"
          busy={remove.isPending}
          error={remove.error?.message}
          onConfirm={() => remove.mutate(removing)}
          onCancel={() => setRemoving(null)}
        >
          <p>
            Si tolgono i movimenti che ha portato. Si può ricaricare quando vuoi; il registro
            attività conserva il caricamento e la rimozione.
          </p>
        </ConfirmDialog>
      )}
    </section>
  );
}

interface ChosenStatement {
  name: string;
  /** The XML as text, or the Excel in base64 (1.37). */
  xml?: string;
  excel?: string;
  /** Several Excel files together, e.g. the exports of Intesa (1.38). */
  files?: { fileName: string; excel: string }[];
}

async function base64Of(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  for (let index = 0; index < bytes.length; index += 8192) {
    binary += String.fromCharCode(...bytes.subarray(index, index + 8192));
  }
  return btoa(binary);
}

/** «Carica estratto conto»: the XML or the Excel checked and counted, then loaded. */
function LoadDialog({ code, onClose }: { code: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [started, setStarted] = useState<string | null>(null);
  const [file, setFile] = useState<ChosenStatement | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const send = (chosen: ChosenStatement, dryRun: boolean) =>
    apiRequest<StatementLoadResult>(`/treasury/banks/${code}/statements`, {
      method: 'POST',
      body: {
        xml: chosen.xml,
        excel: chosen.excel,
        files: chosen.files,
        fileName: chosen.name,
        dryRun,
      },
    });
  const preview = useMutation({
    mutationFn: (chosen: ChosenStatement) => send(chosen, true),
  });
  const load = useMutation({
    mutationFn: () => send(file!, false),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['treasury', 'banks'] });
      await queryClient.invalidateQueries({ queryKey: worklistKeys.all });
      // What came is to settle now, or it is forgotten (3.00).
      if (data.loadId) setStarted(data.loadId);
      else onClose();
    },
  });
  const [choiceError, setChoiceError] = useState('');
  const choose = async (event: ChangeEvent<HTMLInputElement>) => {
    const chosen = [...(event.target.files ?? [])];
    setChoiceError('');
    if (!chosen.length) return;
    let read: ChosenStatement;
    if (chosen.length > 1) {
      if (chosen.some((item) => !/\.xlsx$/i.test(item.name))) {
        setChoiceError('Più file insieme: solo Excel (.xlsx). Un XML si carica da solo.');
        return;
      }
      read = {
        name: `${chosen.length} file`,
        files: await Promise.all(
          chosen.map(async (item) => ({ fileName: item.name, excel: await base64Of(item) })),
        ),
      };
    } else if (/\.xlsx$/i.test(chosen[0]!.name)) {
      read = { excel: await base64Of(chosen[0]!), name: chosen[0]!.name };
    } else read = { xml: await chosen[0]!.text(), name: chosen[0]!.name };
    setFile(read);
    preview.mutate(read);
  };
  const result = preview.data;
  if (started) {
    return (
      <StartWorkDialog loadKey={`banca:${started}`} title="Estratto caricato" onClose={onClose} />
    );
  }
  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Carica estratto conto</h2>
        <p className="muted small">
          Il file XML (CBI) della banca, i «movimenti.xlsx» del portale Intesa (anche molti insieme)
          oppure l’Excel con il foglio «Movimenti» per i periodi che il portale non dà più. Si
          possono caricare file e periodi che si sovrappongono: i movimenti già presenti si
          riconoscono e si saltano; di un Excel «Movimenti» si prendono solo i giorni che nessun
          altro caricamento copre.
        </p>
        <input type="file" accept=".xml,.xlsx" multiple onChange={(event) => void choose(event)} />
        {choiceError && <p className="error">{choiceError}</p>}
        {preview.isPending && <p className="muted">Controllo dei file…</p>}
        {preview.error && <p className="error">{preview.error.message}</p>}
        {result && (
          <dl className="totals">
            <div>
              <dt>Periodo</dt>
              <dd>
                {result.from && result.to
                  ? `${formatDate(result.from)} – ${formatDate(result.to)}`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt>Movimenti nuovi</dt>
              <dd>{result.created}</dd>
            </div>
            <div>
              <dt>Già caricati (saltati)</dt>
              <dd>
                {result.skipped}
                {result.covered > 0 && (
                  <small className="muted block">
                    di cui {result.covered} in giorni già coperti da un altro caricamento
                  </small>
                )}
              </dd>
            </div>
            <div>
              <dt>Saldi della banca</dt>
              <dd>
                {result.balanceMismatches === 0 ? (
                  <span className="pill ok">tornano</span>
                ) : (
                  <span className="pill warn">{result.balanceMismatches} giorni non tornano</span>
                )}
              </dd>
            </div>
          </dl>
        )}
        {result && result.files.length > 1 && (
          <div className="table-scroll load-files">
            <table className="data-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Periodo</th>
                  <th className="numeric">Nuovi</th>
                  <th className="numeric">Già presenti</th>
                </tr>
              </thead>
              <tbody>
                {[...result.files]
                  .sort((a, b) => (a.from ?? '').localeCompare(b.from ?? ''))
                  .map((item, index) => (
                    <tr key={`${item.fileName}-${index}`}>
                      <td>
                        {item.fileName}
                        {item.headerMismatch && (
                          <small className="muted block">
                            i saldi in testa non tornano con le righe: non usati
                          </small>
                        )}
                      </td>
                      <td>
                        {item.from && item.to
                          ? `${formatDate(item.from)} – ${formatDate(item.to)}`
                          : '—'}
                      </td>
                      <td className="numeric">{item.created}</td>
                      <td className="numeric">{item.skipped}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
        {load.error && <p className="error">{load.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={load.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={!result || result.created === 0 || load.isPending}
            onClick={() => load.mutate()}
          >
            {load.isPending ? 'Attendere…' : result?.created === 0 ? 'Niente di nuovo' : 'Carica'}
          </button>
        </div>
      </div>
    </dialog>
  );
}

/**
 * Why the balance does not agree with the bank: the same difference every day
 * means one movement is missing before the first of them (1.39); otherwise
 * probably a statement.
 */
function MismatchNotice({ check }: { check: BankMovementsResponse['check'] }) {
  const first = check.mismatches[0]!;
  const difference = constantDifference(check.mismatches);
  if (difference !== null) {
    const higher = Number(difference) > 0;
    return (
      <p className="notice small">
        Il saldo è {higher ? 'più alto' : 'più basso'} di quello della banca di{' '}
        <strong>{formatMoney(difference.replace('-', ''))}</strong>, sempre la stessa cifra, in{' '}
        {check.mismatches.length} {check.mismatches.length === 1 ? 'giorno' : 'giorni'} dal{' '}
        {formatDate(first.date)}: manca un movimento di{' '}
        {formatMoney(fromCents(-toCents(difference)))} prima di quella data
        {check.start ? ` (dopo il ${formatDate(check.start.date)})` : ''}. Aggiungilo con «Movimento
        a mano».
      </p>
    );
  }
  return (
    <p className="notice small">
      Il saldo non torna con la banca in {check.mismatches.length}{' '}
      {check.mismatches.length === 1 ? 'giorno' : 'giorni'}, dal {formatDate(first.date)} (nostro{' '}
      {formatMoney(first.ours)}, banca {formatMoney(first.bank)}): manca probabilmente un estratto.
    </p>
  );
}

/** «Movimento a mano»: what the bank does not export (1.39). */
function ManualMovementDialog({ code, onClose }: { code: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [form, setForm] = useState({ bookingDate: '', amount: '', description: '' });
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  const number = form.amount.trim() ? parseDecimal(form.amount) : null;
  const save = useMutation({
    mutationFn: () => {
      const body: ManualMovementRequest = {
        bookingDate: form.bookingDate,
        amount: number === null ? '' : fromCents(Math.round(number * 100)),
        description: form.description,
      };
      return apiRequest(`/treasury/banks/${code}/movements`, { method: 'POST', body });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['treasury', 'banks'] });
      onClose();
    },
  });
  return (
    <dialog ref={dialogRef} className="dialog narrow" onClose={onClose}>
      <div className="stack">
        <h2>Movimento a mano</h2>
        <p className="muted small">
          Solo per ciò che la banca non esporta. Entra nel saldo e nel controllo, si riconcilia come
          gli altri e si toglie dall’elenco degli estratti caricati.
        </p>
        <Field label="Data contabile">
          <input
            type="date"
            value={form.bookingDate}
            onChange={(event) => setForm({ ...form, bookingDate: event.target.value })}
          />
        </Field>
        <Field label="Importo" hint="Negativo se esce dal conto: −13,00.">
          <DecimalInput
            value={form.amount}
            onChange={(value) => setForm({ ...form, amount: value })}
            decimals={[2, 2]}
          />
        </Field>
        <Field label="Descrizione">
          <input
            value={form.description}
            maxLength={300}
            onChange={(event) => setForm({ ...form, description: event.target.value })}
          />
        </Field>
        {save.error && <p className="error">{save.error.message}</p>}
        <div className="actions">
          <button type="button" onClick={onClose} disabled={save.isPending}>
            Annulla
          </button>
          <button
            type="button"
            className="primary"
            disabled={
              save.isPending ||
              !form.bookingDate ||
              !form.description.trim() ||
              number === null ||
              number === 0
            }
            onClick={() => save.mutate()}
          >
            {save.isPending ? 'Attendere…' : 'Aggiungi'}
          </button>
        </div>
      </div>
    </dialog>
  );
}
